import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch, useHistory } from 'react-router-dom';

import { TreeNav } from 'ui-components/components/tree-navigation';
import { noop } from 'ui-components/default-props/noop';

import { messages } from './messages';
import DataDrivenLogo from '../../../components/data-driven-logo';
import { SearchForm } from '../../../components/shipping-grid/search-form';
import {
  RETURNS_GRID_SIDEBAR_CLEAR_QUICK_SEARCH,
  RETURNS_GRID_SIDEBAR_QUICK_SEARCH,
  RETURNS_GRID_SIDEBAR_SELECT_STORE,
} from '../../../constants/analytics-events';
import { BASE_RETURNS_PATH } from '../../../routes/constants';
import { Segment } from '../../../services/analytics-services/segment';
import { QUICK_SEARCH_LOCATION_KEY } from '../../../services/quicksearch-location-service';
import Global from '../../../store/global-selectors';
import { returnsSearch } from '../../../store/returns/nav/returns.nav.actions';
import { getReturns } from '../../../store/returns/returns.actions';
import {
  NAVITEM_STANDARD_RETURNS_ID,
  STATUS_OUTSTANDING,
  STATUS_RECEIVED,
  STATUS_VOIDED,
  STATUS_PENDING_APPROVAL,
  PATH_ENHANCED_RETURNS,
  resetPageQueryParam,
  useIsEnhancedReturnsFeatureEnabled,
} from '../../../store/returns/returns.helpers';
import { useEnhancedReturnsCountContext } from '../enhanced-returns-count-context';

const standardReturnsNavItem = {
  id: NAVITEM_STANDARD_RETURNS_ID,
  label: <FormattedMessage {...messages.standardReturnsMessage} />,
};

export const ReturnsLeftNav = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const isEnhancedReturnsFeatureEnabled = useIsEnhancedReturnsFeatureEnabled();
  const stores = useSelector(Global.selectSellerActiveStores);
  const enhancedReturnsMatch = useRouteMatch(`${PATH_ENHANCED_RETURNS}/:storeGuid?`);
  const selectedStoreGuid = enhancedReturnsMatch?.params.storeGuid;

  const standardReturnsMatch = useRouteMatch(`${BASE_RETURNS_PATH}/${NAVITEM_STANDARD_RETURNS_ID}/:statusId?`);
  const selectedStandardReturnsStatus = standardReturnsMatch?.params.statusId;

  const standardReturnsSearchCount = useSelector(Global.selectReturnsNavSearchTotalCount);
  const [returnSearchInputValue, setReturnSearchInputValue] = useState('');
  const standardReturnsPage = useSelector(Global.selectReturnsGridPage);
  const standardReturnsPageSize = useSelector(Global.selectReturnsGridPageSize);

  const buildTagPropsFunc = useMemo(() => {
    const newSearch = resetPageQueryParam(history.location.search);
    const quickSearch = new URLSearchParams(history.location.search).get(QUICK_SEARCH_LOCATION_KEY);
    const standardReturnsParams = new URLSearchParams(`page=${standardReturnsPage}&pageSize=${standardReturnsPageSize}`);
    if (quickSearch) {
      standardReturnsParams.set(QUICK_SEARCH_LOCATION_KEY, quickSearch);
    }

    return (item) => {
      if (item.id === NAVITEM_STANDARD_RETURNS_ID) {
        return {
          to: {
            pathname: `${BASE_RETURNS_PATH}/${NAVITEM_STANDARD_RETURNS_ID}/${STATUS_OUTSTANDING}`,
            search: standardReturnsParams.toString(),
          },
        };
      }

      return {
        to: {
          pathname: `${PATH_ENHANCED_RETURNS}/${item.id}/${STATUS_PENDING_APPROVAL}`,
          search: newSearch,
        },
      };
    };
  }, [standardReturnsPage, standardReturnsPageSize, history.location.search]);

  const { onSearch: onEnhancedReturnsSearch, countsPerStoreMap, isSearchMode, onClearSearchMode } = useEnhancedReturnsCountContext();

  const onSearch = useCallback(
    (searchData) => {
      Segment.trackEvent(RETURNS_GRID_SIDEBAR_QUICK_SEARCH, {
        type: RETURNS_GRID_SIDEBAR_QUICK_SEARCH,
        searchData,
      });

      dispatch(returnsSearch(searchData));
      onEnhancedReturnsSearch(searchData.searchTerm);
    },
    [dispatch, onEnhancedReturnsSearch],
  );

  const onClearSearchInput = useCallback(() => {
    Segment.trackEvent(RETURNS_GRID_SIDEBAR_CLEAR_QUICK_SEARCH, {
      type: RETURNS_GRID_SIDEBAR_CLEAR_QUICK_SEARCH,
    });

    setReturnSearchInputValue('');
    onClearSearchMode();
  }, [onClearSearchMode]);

  const resetGrid = useCallback(() => {
    dispatch(getReturns());
  }, [dispatch]);

  const enhancedReturnsEligibleStores = stores.filter((s) => s.supportsEnhancedReturns);
  const navItems = useMemo(() => {
    const items = [];

    items.push({
      ...standardReturnsNavItem,
      selected: [STATUS_OUTSTANDING, STATUS_RECEIVED, STATUS_VOIDED].includes(selectedStandardReturnsStatus),
      count: isSearchMode ? standardReturnsSearchCount : undefined,
    });

    if (enhancedReturnsEligibleStores.length && isEnhancedReturnsFeatureEnabled) {
      enhancedReturnsEligibleStores.forEach((store) => {
        const totalCount = countsPerStoreMap[store.storeGuid]?.totalCount || 0;

        items.push({
          id: store.storeGuid,
          label: store.name,
          selected: selectedStoreGuid === store.storeGuid,
          count: totalCount,
          shouldRenderAnIcon: true,
          customIcon: <DataDrivenLogo logoType='icon' marketplaceId={store.marketplaceId} />,
        });
      });
    }

    return items;
  }, [
    countsPerStoreMap,
    enhancedReturnsEligibleStores,
    isEnhancedReturnsFeatureEnabled,
    isSearchMode,
    selectedStandardReturnsStatus,
    selectedStoreGuid,
    standardReturnsSearchCount,
  ]);

  const onItemSelected = useCallback(
    (item) => {
      Segment.trackEvent(RETURNS_GRID_SIDEBAR_SELECT_STORE, {
        type: RETURNS_GRID_SIDEBAR_SELECT_STORE,
        item,
      });

      history.push(`${BASE_RETURNS_PATH}/${item.id}`);
    },
    [history],
  );

  return (
    <div>
      <SearchForm
        ariaLabelMessageDescriptor={messages.searchLabel}
        clearSearchInput={onClearSearchInput}
        formTitleMessageDescriptor={messages.searchLabel}
        handleSearchInputChange={setReturnSearchInputValue}
        isHighlighted={isSearchMode}
        onSearch={onSearch}
        placeholderMessageDescriptor={messages.searchPlaceholder}
        resetGrid={resetGrid}
        searchInputValue={returnSearchInputValue}
      />
      <TreeNav
        buildTagProps={buildTagPropsFunc}
        isHighlighted={isSearchMode}
        items={navItems}
        onExpandClick={noop}
        onItemSelected={onItemSelected}
      />
    </div>
  );
};
